import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle, BadgeCheck, ChevronDown, ExternalLink, RefreshCcw, Search, Star,
} from 'lucide-react'
import Header from '@/components/layout/Header'
import { Button } from '@/components/ui/button'
import { useLanguage } from '@/contexts/LanguageContext'

/**
 * Dashboard → Management → Supporters.
 *
 * The staff-side view of Whop memberships, mirrored locally by the backend's
 * reconcile pass (backend/supporters/sync.py). Read-only on purpose: Whop owns
 * the subscription and its Discord app owns the role, so there is nothing here
 * to click that could change either. What this page answers is "who is paying,
 * on which Steam id, and since when" — the questions the reserved-slot work
 * actually runs on.
 *
 * This is the one place the SteamID64 is printed alongside a name, the same
 * line the members page draws: staff data, never member data.
 */

interface SupporterRow {
  membership_id: string
  discord_id: string
  discord_name: string
  discord_avatar: string
  steam_id: string
  steam_name: string
  steam_verified: boolean
  status: string
  active: boolean
  cancel_at_period_end: boolean
  plan_id: string
  product_name: string
  price: string
  currency: string
  email: string
  manage_url: string
  started_at: number
  renews_at: number
  first_seen: number
  last_seen: number
  notified_at: number
  raw: Record<string, unknown>
}

type Filter = 'active' | 'inactive' | 'all'

const FALLBACK_AVATAR = 'https://cdn.discordapp.com/embed/avatars/0.png'

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">{label}</p>
      <p className="mt-0.5 break-words text-sm">{children}</p>
    </div>
  )
}

export default function Supporters() {
  const { T, lang } = useLanguage()

  const [rows, setRows] = useState<SupporterRow[]>([])
  const [configured, setConfigured] = useState(true)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<Filter>('active')
  const [search, setSearch] = useState('')
  const [openRaw, setOpenRaw] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/supporters', { credentials: 'include' })
      if (!res.ok) { setError(T.supporters.loadError); return }
      const data = await res.json()
      setRows(data.supporters ?? [])
      setConfigured(Boolean(data.configured))
    } catch {
      setError(T.supporters.loadError)
    } finally {
      setLoading(false)
    }
  }, [T.supporters.loadError])

  useEffect(() => { load() }, [load])

  /** Pull Whop now rather than waiting out the reconcile interval. */
  const resync = async () => {
    setSyncing(true)
    setError(null)
    try {
      const res = await fetch('/api/supporters/refresh', {
        method: 'POST', credentials: 'include',
      })
      if (!res.ok) { setError(T.supporters.syncError); return }
      await load()
    } catch {
      setError(T.supporters.syncError)
    } finally {
      setSyncing(false)
    }
  }

  const locale = lang === 'tr' ? 'tr-TR' : 'en-GB'
  const formatDate = (ts: number) =>
    ts
      ? new Date(ts * 1000).toLocaleDateString(locale, {
          day: 'numeric', month: 'short', year: 'numeric',
        })
      : '-'

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows
      .filter(r => filter === 'all' || (filter === 'active' ? r.active : !r.active))
      .filter(r =>
        !q ||
        r.discord_name.toLowerCase().includes(q) ||
        r.steam_name.toLowerCase().includes(q) ||
        r.steam_id.includes(q) ||
        r.discord_id.includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.membership_id.toLowerCase().includes(q),
      )
  }, [rows, filter, search])

  const filters: { key: Filter; label: string; count: number }[] = [
    { key: 'active',   label: T.supporters.filters.active,   count: rows.filter(r => r.active).length },
    { key: 'inactive', label: T.supporters.filters.inactive, count: rows.filter(r => !r.active).length },
    { key: 'all',      label: T.supporters.filters.all,      count: rows.length },
  ]

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <Header title={T.supporters.title} />

      <div className="flex-1 space-y-5 overflow-y-auto p-4 md:p-6">
        <p className="max-w-3xl text-[13px] leading-relaxed text-muted-foreground">
          {T.supporters.lead}
        </p>

        {/* Whop with no API key reads as "nobody has ever bought anything",
            which is the same empty list as a working install with no supporters
            yet. Say which one it is rather than leaving staff to guess. */}
        {!configured && (
          <p className="flex items-start gap-2 rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {T.supporters.notConfigured}
          </p>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-1.5">
            {filters.map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`cursor-pointer rounded-sm border px-3 py-1.5 font-display text-[13px] font-semibold tracking-tight transition-colors ${
                  filter === f.key
                    ? 'border-primary/60 bg-primary/15 text-foreground'
                    : 'border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10'
                }`}
              >
                {f.label}
                <span className="ml-1.5 text-[11px] text-muted-foreground/70">{f.count}</span>
              </button>
            ))}
          </div>

          <div className="relative ml-auto max-w-xs flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder={T.supporters.searchPlaceholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-white/5 py-1.5 pl-8 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-white/20"
            />
          </div>

          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 text-xs"
            onClick={resync}
            disabled={syncing || loading}
          >
            <RefreshCcw className={`h-3 w-3 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? T.supporters.syncing : T.supporters.refresh}
          </Button>
        </div>

        {error && (
          <p className="rounded-sm border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        {loading && rows.length === 0 && (
          <p className="text-sm text-muted-foreground">{T.supporters.loading}</p>
        )}

        {!loading && visible.length === 0 && (
          <p className="text-sm text-muted-foreground">{T.supporters.empty}</p>
        )}

        <div className="space-y-4">
          {visible.map(row => (
            <div key={row.membership_id} className="rounded-md border border-white/10 bg-card/80 p-4 md:p-5">
              {/* Head */}
              <div className="flex flex-wrap items-start gap-3">
                <img
                  src={row.discord_avatar || FALLBACK_AVATAR}
                  alt=""
                  className="h-11 w-11 shrink-0 rounded-full object-cover ring-2 ring-white/10"
                  onError={e => { (e.target as HTMLImageElement).src = FALLBACK_AVATAR }}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-display text-base font-bold tracking-tight">
                      {row.discord_name || T.supporters.unknownBuyer}
                    </h3>
                    <span
                      className={`rounded-sm border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                        row.active
                          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                          : 'border-white/15 bg-white/5 text-muted-foreground'
                      }`}
                    >
                      {row.status || (row.active ? T.supporters.active : T.supporters.inactive)}
                    </span>
                    {row.cancel_at_period_end && (
                      <span className="rounded-sm border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300">
                        {T.supporters.cancelling}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 truncate font-mono text-[12px] text-muted-foreground">
                    {row.discord_id || '-'}
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground/70">
                    {T.supporters.startedOn.replace('{date}', formatDate(row.started_at))}
                    {row.renews_at
                      ? ` · ${T.supporters.renewsOn.replace('{date}', formatDate(row.renews_at))}`
                      : ''}
                  </p>
                </div>

                {row.manage_url && (
                  <a href={row.manage_url} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs">
                      {T.supporters.openWhop}
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                  </a>
                )}
              </div>

              {/* Details */}
              <div className="mt-4 grid gap-3 border-t border-white/8 pt-4 sm:grid-cols-2 lg:grid-cols-4">
                <Detail label={T.supporters.fields.steamName}>
                  {row.steam_name || (
                    <span className="text-muted-foreground">{T.supporters.noSteamName}</span>
                  )}
                </Detail>

                {/* The id and its proof travel together: an unverified one must
                    not be pushed to a reserved slot, so the warning belongs on
                    the same line staff would copy the number from. */}
                <Detail label={T.supporters.fields.steamId}>
                  <span className="font-mono text-[13px]">{row.steam_id || '-'}</span>
                  {row.steam_id && (
                    <span
                      className={`ml-2 inline-flex items-center gap-1 align-middle text-[11px] ${
                        row.steam_verified ? 'text-emerald-400' : 'text-amber-400'
                      }`}
                    >
                      {row.steam_verified
                        ? <><BadgeCheck className="h-3.5 w-3.5" />{T.supporters.verified}</>
                        : <><AlertTriangle className="h-3.5 w-3.5" />{T.supporters.unverified}</>}
                    </span>
                  )}
                </Detail>

                <Detail label={T.supporters.fields.product}>
                  {row.product_name || '-'}
                </Detail>
                <Detail label={T.supporters.fields.price}>
                  {[row.price, row.currency].filter(Boolean).join(' ') || '-'}
                </Detail>

                <Detail label={T.supporters.fields.email}>
                  {row.email || '-'}
                </Detail>
                <Detail label={T.supporters.fields.membershipId}>
                  <span className="font-mono text-[12px]">{row.membership_id}</span>
                </Detail>
                <Detail label={T.supporters.fields.planId}>
                  <span className="font-mono text-[12px]">{row.plan_id || '-'}</span>
                </Detail>
                <Detail label={T.supporters.fields.notified}>
                  {row.notified_at
                    ? formatDate(row.notified_at)
                    : <span className="text-amber-400">{T.supporters.notNotified}</span>}
                </Detail>
              </div>

              {/* Whop spells some fields differently across plan types, so the
                  whole payload is kept and shown. Anything the columns above
                  guessed wrong is still readable here. */}
              <button
                onClick={() => setOpenRaw(openRaw === row.membership_id ? null : row.membership_id)}
                className="mt-4 flex cursor-pointer items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground"
              >
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform ${openRaw === row.membership_id ? 'rotate-180' : ''}`}
                />
                {T.supporters.rawToggle}
              </button>
              {openRaw === row.membership_id && (
                <pre className="mt-2 max-h-80 overflow-auto rounded-sm border border-white/10 bg-black/40 p-3 text-[11px] leading-relaxed text-muted-foreground">
                  {JSON.stringify(row.raw, null, 2)}
                </pre>
              )}
            </div>
          ))}
        </div>

        {rows.length > 0 && (
          <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground/60">
            <Star className="h-3 w-3" />
            {T.supporters.footnote}
          </p>
        )}
      </div>
    </div>
  )
}
